/**
 * Small RFC 4180 CSV reader and writer for bulk import.
 *
 * Reads what spreadsheet apps and Google Forms export: quoted fields,
 * doubled quotes inside quotes, newlines inside quotes, CRLF / LF / CR line
 * ends, a UTF-8 byte-order mark, and comma, semicolon or tab separators
 * (European Excel saves with semicolons).
 */

export type Delimiter = ',' | ';' | '\t';

const CANDIDATES: Delimiter[] = [',', ';', '\t'];

/**
 * Guess the separator from the first few lines: the candidate that appears
 * outside quotes most consistently wins. Commas win ties.
 */
export function detectDelimiter(text: string): Delimiter {
  const sample = text.slice(0, 20_000);
  let best: Delimiter = ',';
  let bestScore = 0;
  for (const d of CANDIDATES) {
    const rows = parseCsv(sample, d).slice(0, 20);
    if (rows.length === 0) continue;
    const counts = rows.map((r) => r.length - 1);
    const max = Math.max(...counts);
    if (max === 0) continue;
    // Lines that share the most common field count.
    const freq = new Map<number, number>();
    for (const c of counts) freq.set(c, (freq.get(c) ?? 0) + 1);
    let modeCount = 0;
    let mode = 0;
    for (const [c, n] of freq) {
      if (c > 0 && (n > modeCount || (n === modeCount && c > mode))) {
        modeCount = n;
        mode = c;
      }
    }
    const score = modeCount * 1000 + mode;
    if (score > bestScore) {
      bestScore = score;
      best = d;
    }
  }
  return best;
}

/** Parse CSV text into rows of cells. Fully blank lines are dropped. */
export function parseCsv(input: string, delimiter?: Delimiter): string[][] {
  const text = input.charCodeAt(0) === 0xfeff ? input.slice(1) : input;
  const sep = delimiter ?? detectDelimiter(text);
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;
  let i = 0;
  const n = text.length;

  const endField = (): void => {
    row.push(field);
    field = '';
  };
  const endRow = (): void => {
    endField();
    if (!(row.length === 1 && row[0] === '')) rows.push(row);
    row = [];
  };

  while (i < n) {
    const ch = text[i]!;
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i += 2;
          continue;
        }
        inQuotes = false;
        i += 1;
        continue;
      }
      field += ch;
      i += 1;
      continue;
    }
    if (ch === '"' && field.trim() === '') {
      // A quote opens a quoted field only at the start of a field (leading
      // spaces allowed). A stray quote mid-field is kept as text.
      field = '';
      inQuotes = true;
      i += 1;
      continue;
    }
    if (ch === sep) {
      endField();
      i += 1;
      continue;
    }
    if (ch === '\r' || ch === '\n') {
      endRow();
      i += ch === '\r' && text[i + 1] === '\n' ? 2 : 1;
      continue;
    }
    field += ch;
    i += 1;
  }
  if (field !== '' || row.length > 0) endRow();
  return rows;
}

/** One CSV cell, quoted when it needs to be. Also defuses spreadsheet formulas. */
export function csvCell(value: unknown): string {
  let s = value === null || value === undefined ? '' : String(value);
  // A leading = + - @ makes Excel / Sheets evaluate the cell as a formula.
  if (/^[=+\-@]/.test(s)) s = `'${s}`;
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/** Rows → CSV text with CRLF line ends (what Excel expects). */
export function toCsv(rows: ReadonlyArray<ReadonlyArray<unknown>>): string {
  return rows.map((r) => r.map(csvCell).join(',')).join('\r\n');
}
